import { captureException } from "@sentry/browser";
import type { BuilderLocationSettings } from "api-types/builders/builderTypes";
import type { ClientCommunity } from "api-types/communities/communitiesResponses";
import type { ClientLocationPlan } from "api-types/locationPlans/locationPlansResponses";
import type { ClientLocationWithLevel } from "api-types/locations/locationBaseTypes";
import { getLocationPlanName } from "api-types/locations/locationDisplay";
import type { ClientLotCommunityPlanHydrated } from "api-types/lotCommunityPlans/lotCommunityPlansResponses";
import type { ClientLot, ClientLotHydrated } from "api-types/lots/lotsResponses";
import type { ClientMasterPlan } from "api-types/masterPlans/masterPlansResponses";
import type { MasterPlan } from "api-types/modelTypes";
import { WEB_API_BASE_PATH } from "common/constants";
import { assertIsNotNull, assertIsNotNullOrUndefined, ensureDefined } from "common/utils/assertOfType";
import objectIsEmpty from "common/utils/objectIsEmpty";
import { snakeCaseToTitleCase } from "common/utils/strings";
import { assertValidatesSchemaTODO } from "doc/schema/docSchemaAssertion";
import {
  DownloadCommunityPlanModal,
  DownloadMasterPlanModal,
} from "lib/components/builders/communities/plans/modals/DownloadPlanModal";
import usePlanModalState from "lib/components/builders/communities/plans/modals/usePlanModalState";
import type { NonZeroBasePriceOptions } from "lib/components/builders/ConfirmNonZeroBasePriceOptions";
import { ConfirmNonZeroBasePriceOptions } from "lib/components/builders/ConfirmNonZeroBasePriceOptions";
import EditMasterPlanModal from "lib/components/builders/EditMasterPlanModal";
import type { MasterCommunityOrLotPlan } from "lib/components/builders/EditPlanModal";
import EditPlanModal from "lib/components/builders/EditPlanModal";
import usePlanOverflowMenuItems from "lib/components/builders/hooks/usePlanOverflowMenuItems";
import { planLibraryActions } from "lib/components/builders/plan-library/planLibraryReducer";
import { usePlanLibraryDispatch, usePlanLibrarySelector } from "lib/components/builders/plan-library/planLibraryStore";
import { planLibraryGetNonZeroBasePriceOptions } from "lib/components/builders/plan-library/planLibraryUtils";
import {
  ARCHIVE_PLAN_MESSAGE,
  DELETE_COMMUNITY_PLAN_MESSAGE,
  DELETE_MASTER_PLAN_MESSAGE,
} from "lib/constants/messages";
import {
  getBuilderLocationPlanURL,
  getBuilderLotsURL,
  getLotCommunityPlanByCommunityPlanIdURL,
  getLotCommunityPlansURL,
  getMasterPlanHydratedURL,
} from "lib/endpointHelpers";
import { fetcher } from "lib/fetcher";
import type { PublicPlanType } from "lib/requestTypes";
import {
  editShowroomIntroPath,
  locationPlanLibraryPath,
  locationPlanShowroomPath,
  lotCommunityPlanLibraryPath,
  lotCommunityPlanShowroomPath,
  masterPlanLibraryPath,
  masterPlanShowroomPath,
  procurePath,
  showroomIntroPath,
  studioPath,
  useAppRouterParams,
} from "lib/routeHelpers";
import { useUserContext } from "lib/state/UserContextProvider";
import {
  archiveCommunityPlan,
  deleteCommunityPlan,
  unarchiveCommunityPlan,
  updateLocationPlanOverrides,
} from "lib/storage/locationPlanStorage";
import { fetchAllLotPages, updateLotCommunityPlan } from "lib/storage/lotStorage";
import {
  archiveMasterPlan,
  deleteMasterPlan,
  unarchiveMasterPlan,
  updateMasterPlanOverrides,
} from "lib/storage/masterPlanStorage";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { mutate } from "swr";
import Button from "ui/buttons/Button";
import ArchivePlanModal from "ui/components/ArchivePlanModal";
import ConfirmDeleteModal from "ui/components/ConfirmDeleteModal";
import { SimpleDropDownMenu } from "ui/components/DropdownMenu";
import type { OptionProps } from "ui/forms/Select";
import Select from "ui/forms/Select";
import useAppContext from "ui/hooks/useAppContext";
import { useAppNavigate } from "ui/hooks/useAppNavigate";
import useNavigationPrompt from "ui/hooks/useNavigationPrompt";
import IconBuster from "ui/icons/IconBuster";
import Tooltip from "ui/popovers/Tooltip";
import styles from "./PlanLibraryHeaderActions.module.scss";

interface RoutableOptionProps extends OptionProps {
  pathToRouteTo?: string;
}

async function createDropdownMenuForMasterPlanOverrides({
  masterPlanId,
  masterPlanName,
  builderId,
}: {
  masterPlanId: string;
  masterPlanName: string | null | undefined;
  builderId: string;
}): Promise<RoutableOptionProps[]> {
  const tabName = location.hash?.substring(1) ? `${location.hash?.substring(1)}` : "";
  const menu: RoutableOptionProps[] = [
    { label: "Master Plan", value: "header_master_plan", displayBehavior: "header" },
    {
      label: masterPlanName || "Master Plan",
      value: masterPlanId,
      pathToRouteTo: masterPlanLibraryPath(builderId, masterPlanId, tabName),
    },
  ];

  try {
    const communityPlans = await fetcher<ClientLocationPlan[]>(
      `${WEB_API_BASE_PATH}/builders/${builderId}/master-plans/${masterPlanId}/community-plans`,
    );
    if (communityPlans.length > 0) {
      menu.push({ label: "Plans", value: "header_community_plans", displayBehavior: "header" });

      communityPlans.forEach((cp) => {
        assertIsNotNullOrUndefined(cp.locationId);

        menu.push({
          label: cp.name,
          value: `${cp.id} ${cp.locationId}`,
          pathToRouteTo: locationPlanLibraryPath({
            builderId,
            locationId: cp.locationId,
            locationPlanId: cp.id,
            tab: tabName,
          }),
        });
      });
    }
  } catch (e) {
    captureException(e);
  }

  return menu;
}

async function createDropdownMenuForCommunityPlanOverrides({
  communityPlanId,
  communityPlanName,
  masterPlanId,
  masterPlanName,
  community,
  builderId,
}: {
  communityPlanId: string;
  communityPlanName: string;
  masterPlanId: string;
  masterPlanName: string | null;
  community: ClientCommunity;
  builderId: string;
}): Promise<RoutableOptionProps[]> {
  const tabName = location.hash?.substring(1) ? `${location.hash?.substring(1)}` : "";
  const menu: RoutableOptionProps[] = [
    { label: "Master Plan", value: "header_master_plan", displayBehavior: "header" },
    {
      label: masterPlanName || "Master Plan",
      value: masterPlanId,
      pathToRouteTo: masterPlanLibraryPath(builderId, masterPlanId, tabName),
    },
    { label: "Plan", value: "header_community_plans", displayBehavior: "header" },
    {
      label: communityPlanName || "Plan",
      value: communityPlanId,
      pathToRouteTo: locationPlanLibraryPath({
        builderId,
        locationId: community.id,
        locationPlanId: communityPlanId,
        tab: tabName,
      }),
    },
  ];

  try {
    const lots = await fetchAllLotPages<ClientLotHydrated>((pagination) =>
      getBuilderLotsURL({ builderId, locationId: community.id, locationPlanId: communityPlanId, pagination }),
    );
    if (lots.length > 0) {
      menu.push({ label: "Lot Plans", value: "header_lot_community_plans", displayBehavior: "header" });
      lots.forEach((lot) => {
        const matchingLCP = lot.lotCommunityPlans.find((lcp) => lcp.communityPlanId === communityPlanId);
        menu.push({
          label: matchingLCP?.name || `${masterPlanName} on Lot ${lot.lotNumber}`,
          value: matchingLCP?.id || lot.id,
          pathToRouteTo: lotCommunityPlanLibraryPath(
            builderId,
            masterPlanId,
            community.id,
            communityPlanId,
            lot.id,
            tabName,
          ),
        });
      });
    }
  } catch (e) {
    captureException(e);
  }

  return menu;
}

async function createDropdownMenuForLotCommunityPlanOverrides({
  lotCommunityPlanId,
  lotCommunityPlanName,
  communityPlanId,
  communityPlanName,
  masterPlanId,
  masterPlanName,
  community,
  lot,
  builderId,
  builderSettings,
}: {
  communityPlanId: string;
  communityPlanName: string;
  masterPlanId: string;
  masterPlanName: string | undefined | null;
  community: ClientLocationWithLevel;
  lot: ClientLot;
  builderId: string;
  lotCommunityPlanId: string;
  lotCommunityPlanName?: string | null;
  builderSettings?: BuilderLocationSettings | null;
}): Promise<RoutableOptionProps[]> {
  const tabName = location.hash?.substring(1) ? `${location.hash?.substring(1)}` : "";
  const locationPlanName = getLocationPlanName(community, builderSettings);
  const menu: RoutableOptionProps[] = [
    { label: "Master Plan", value: "header_master_plan", displayBehavior: "header" },
    {
      label: masterPlanName || "Master Plan",
      value: masterPlanId,
      pathToRouteTo: masterPlanLibraryPath(builderId, masterPlanId, tabName),
    },
    { label: `${locationPlanName}`, value: "header_community_plan", displayBehavior: "header" },
    {
      label: communityPlanName || `${locationPlanName}`,
      value: communityPlanId,
      pathToRouteTo: locationPlanLibraryPath({
        builderId,
        locationId: community.id,
        locationPlanId: communityPlanId,
        tab: tabName,
      }),
    },
    { label: `Lot ${locationPlanName}`, value: "header_lot_community_plan", displayBehavior: "header" },
    {
      label: lotCommunityPlanName || `${masterPlanName} on Lot ${lot.lotNumber}`,
      value: lotCommunityPlanId,
      pathToRouteTo: lotCommunityPlanLibraryPath(
        builderId,
        masterPlanId,
        community.id,
        communityPlanId,
        lot.id,
        tabName,
      ),
    },
  ];

  try {
    const lotCommunityPlans = await fetcher<ClientLotCommunityPlanHydrated[]>(
      getLotCommunityPlansURL({ builderId, locationId: community.id, lotId: lot.id }),
    );
    const filteredLCPs = lotCommunityPlans.filter((lcp) => lcp.communityPlanId !== communityPlanId);
    if (filteredLCPs.length > 0) {
      menu.push({
        label: `Other plans on Lot ${lot.lotNumber}`,
        value: "header_other_lot_community_plans",
        displayBehavior: "header",
      });
      filteredLCPs.forEach((lcp) => {
        menu.push({
          label: lcp.name || `${lcp.communityPlan.masterPlan.name} on Lot ${lot.lotNumber}`,
          value: lcp.id,
          pathToRouteTo: lotCommunityPlanLibraryPath(
            builderId,
            lcp.communityPlan.masterPlan.id,
            lcp.communityPlan.community?.id as string,
            lcp.communityPlanId,
            lot.id,
            tabName,
          ),
        });
      });
    }
  } catch (e) {
    captureException(e);
  }

  return menu;
}

interface PlanLibraryHeaderActionsProps {
  lotsTableIsParentPage?: boolean;
}

export default function PlanLibraryHeaderActions({}: PlanLibraryHeaderActionsProps) {
  const { setUserNotification } = useAppContext();
  const { builder } = useUserContext();
  const navigate = useAppNavigate();

  const [nonZeroBasePriceOptions, setNonZeroBasePriceOptions] = useState<NonZeroBasePriceOptions>([]);

  const { builderId = "" } = useAppRouterParams();

  const [modalScenario, setModalScenario] = usePlanModalState();
  const [dropdownMenu, setDropdownMenu] = useState<RoutableOptionProps[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const {
    nonBoolOptionSetsPerSalesCategory,
    booleanOptionSetsPerSalesCategory,
    isAnythingOverridden,
    plans,
    studioDocId,
    needsSaving,
    isReadOnly,
    overrides,
    optionEffectOverrides,
    planId,
  } = usePlanLibrarySelector((state) => ({
    nonBoolOptionSetsPerSalesCategory: state.nonBoolOptionSetsPerSalesCategory,
    booleanOptionSetsPerSalesCategory: state.booleanOptionSetsPerSalesCategory,

    isAnythingOverridden: !objectIsEmpty(state.overrides) || !objectIsEmpty(state.optionEffectOverrides),

    plans: state.plans,

    studioDocId: state.plans.masterPlan.studioDocId,
    needsSaving: state.needsSaving,
    isReadOnly: state.isReadOnly,
    overrides: state.overrides,
    optionEffectOverrides: state.optionEffectOverrides,
    planId: state.planId,
  }));

  const dispatch = usePlanLibraryDispatch();

  useNavigationPrompt({ enable: needsSaving });

  useEffect(() => {
    async function asyncFn() {
      switch (plans.planType) {
        case "master_plan":
          setDropdownMenu(
            await createDropdownMenuForMasterPlanOverrides({
              masterPlanId: plans.masterPlan.id,
              masterPlanName: plans.masterPlan.name,
              builderId,
            }),
          );
          break;
        case "community_plan":
          setDropdownMenu(
            await createDropdownMenuForCommunityPlanOverrides({
              communityPlanId: plans.locationPlan.id,
              communityPlanName: plans.locationPlan.name,
              masterPlanId: plans.masterPlan.id,
              masterPlanName: plans.masterPlan.name !== undefined ? plans.masterPlan.name : null,
              community: plans.location,
              builderId,
            }),
          );
          break;
        case "lot":
          setDropdownMenu(
            await createDropdownMenuForLotCommunityPlanOverrides({
              lotCommunityPlanId: plans.lotCommunityPlan.id,
              lotCommunityPlanName: plans.lotCommunityPlan.name,
              communityPlanId: plans.locationPlan.id,
              communityPlanName: plans.locationPlan.name,
              masterPlanId: plans.masterPlan.id,
              masterPlanName: plans.masterPlan.name !== undefined ? plans.masterPlan.name : null,
              community: plans.location,
              lot: plans.lot,
              builderId,
              builderSettings: builder?.locationSettings,
            }),
          );
          break;
        default:
          break;
      }
    }
    void asyncFn();
  }, [plans.planType]);

  // Make sure to invalidate the cached plan data so navigating back to this plan re-seeds the editor
  // from the freshly-saved overrides rather than a stale SWR copy.
  async function saveOverrides() {
    try {
      setIsSaving(true);
      switch (plans.planType) {
        case "master_plan":
          await updateMasterPlanOverrides(builderId, plans.masterPlan.id, overrides, optionEffectOverrides);
          void mutate(getMasterPlanHydratedURL({ builderId, masterPlanId: plans.masterPlan.id }));
          break;

        case "community_plan":
          await updateLocationPlanOverrides(
            builderId,
            plans.location.id,
            plans.locationPlan.id,
            overrides,
            optionEffectOverrides,
          );
          void mutate(
            getBuilderLocationPlanURL({
              builderId,
              locationId: plans.location.id,
              locationPlanId: plans.locationPlan.id,
            }),
          );
          break;
        case "lot":
          await updateLotCommunityPlan({
            builderId,
            locationId: plans.location.id,
            lotId: plans.lot.id,
            lotCommunityPlanId: plans.lotCommunityPlan.id,
            lotCommunityPlanName: `${plans.locationPlan.name} on Lot ${plans.lot.lotNumber}`,
            overrides,
          });
          void mutate(
            getLotCommunityPlanByCommunityPlanIdURL({
              builderId,
              locationId: plans.location.id,
              lotId: plans.lot.id,
              communityPlanId: plans.locationPlan.id,
            }),
          );
          break;
        default:
          break;
      }
      setUserNotification({
        messageType: "success",
        message: `All overrides for ${plan?.name ?? "lot"} have been saved`,
      });
    } catch (error) {
      console.error(error);
      setUserNotification({
        messageType: "error",
        message: `An error occurred attempting to save overrides for ${plan?.name ?? "lot"}`,
      });
    } finally {
      dispatch(planLibraryActions.setNeedsSaving(false));
      setIsSaving(false);
    }
  }

  async function archivePlan(e: React.FormEvent) {
    e.preventDefault();
    setIsArchiving(true);

    try {
      let error: Parameters<typeof setUserNotification>[0] = null;

      switch (plans.planType) {
        case "master_plan": {
          const [, archiveError] = await archiveMasterPlan(builderId, plans.masterPlan.id);
          error = archiveError;
          break;
        }
        case "community_plan": {
          const [, archiveError] = await archiveCommunityPlan(builderId, plans.location.id, plans.locationPlan.id);
          error = archiveError;
          break;
        }
        default:
          break;
      }

      if (error) {
        setIsArchiving(false);
        setUserNotification(error);
        return;
      }

      setUserNotification({
        messageType: "success",
        message: `${snakeCaseToTitleCase(plans.planType)} archived successfully.`,
      });
      setModalScenario(undefined);
      setIsArchiving(false);

      // Refresh the page to show updated state
      window.location.reload();
    } catch (error) {
      console.error(error);
      setIsArchiving(false);
      setUserNotification({
        messageType: "error",
        message: `An error occurred while archiving the ${snakeCaseToTitleCase(plans.planType)}.`,
      });
    }
  }

  async function unarchivePlan(e: React.FormEvent) {
    e.preventDefault();
    setIsArchiving(true);

    try {
      let error: Parameters<typeof setUserNotification>[0] = null;

      switch (plans.planType) {
        case "master_plan": {
          const [, unarchiveError] = await unarchiveMasterPlan(builderId, plans.masterPlan.id);
          error = unarchiveError;
          break;
        }
        case "community_plan": {
          const [, unarchiveError] = await unarchiveCommunityPlan(builderId, plans.location.id, plans.locationPlan.id);
          error = unarchiveError;
          break;
        }
        default:
          break;
      }

      if (error) {
        setIsArchiving(false);
        setUserNotification(error);
        return;
      }

      setUserNotification({
        messageType: "success",
        message: `${snakeCaseToTitleCase(plans.planType)} unarchived successfully.`,
      });
      setModalScenario(undefined);
      setIsArchiving(false);

      // Refresh the page to show updated state
      window.location.reload();
    } catch (error) {
      console.error(error);
      setIsArchiving(false);
      setUserNotification({
        messageType: "error",
        message: `An error occurred while unarchiving the ${snakeCaseToTitleCase(plans.planType)}.`,
      });
    }
  }

  const isArchived = (() => {
    switch (plans.planType) {
      case "master_plan":
        return !!plans.masterPlan.archivedAt;
      case "community_plan":
        return !!plans.locationPlan.archivedAt;
      default:
        return false;
    }
  })();

  const { updatePlan, deletePlan, canArchive, canUnarchive, publicPlanType, showroomPath, plan } = (() => {
    switch (plans.planType) {
      case "master_plan":
        return {
          updatePlan: (plan: MasterCommunityOrLotPlan) => {
            if (plan.name !== plans.masterPlan.name) {
              setDropdownMenu(
                dropdownMenu
                  .map((item) => {
                    if (item.value?.toString().startsWith(planId)) {
                      return { ...item, label: plan.name };
                    } else {
                      return item;
                    }
                  })
                  .filter((x) => !!x) as RoutableOptionProps[],
              );
            }
            assertValidatesSchemaTODO<ClientMasterPlan>(plan);
            dispatch(planLibraryActions.modifyMasterPlan(plan));
            setUserNotification({
              messageType: "success",
              message: "Master plan updated",
            });
          },

          deletePlan: async () => {
            setIsDeleting(true);
            await deleteMasterPlan(builderId, plans.masterPlan.id);
            await navigate({ to: masterPlanLibraryPath(builderId) });
            setIsDeleting(false);
          },

          canArchive: !isArchived,
          canUnarchive: isArchived,

          publicPlanType: "master_plans" as PublicPlanType,
          showroomPath: masterPlanShowroomPath(builderId, plans.masterPlan.id),
          plan: plans.masterPlan,
        };

      case "community_plan":
        return {
          updatePlan: (plan: MasterCommunityOrLotPlan) => {
            assertValidatesSchemaTODO<ClientLocationPlan>(plan);
            if (plan.name !== plans.locationPlan.name) {
              setDropdownMenu(
                dropdownMenu.map((item) => {
                  if (item.value?.toString().startsWith(planId)) {
                    return { ...item, label: plan.name };
                  } else {
                    return item;
                  }
                }),
              );
            }
            dispatch(planLibraryActions.modifyCommunityPlan(plan));
            setUserNotification({
              messageType: "success",
              message: "Community plan updated",
            });
          },

          deletePlan: async () => {
            setIsDeleting(true);
            await deleteCommunityPlan(builderId, plans.location.id, plans.locationPlan.id);
            await navigate({ to: masterPlanLibraryPath(builderId, plans.masterPlan.id) });
            setIsDeleting(false);
          },

          canArchive: !isArchived,
          canUnarchive: isArchived,

          publicPlanType: "community_plans" as PublicPlanType,
          showroomPath: locationPlanShowroomPath(builderId, plans.location.id, plans.locationPlan.id),
          plan: plans.locationPlan,
        };

      case "lot":
        return {
          updatePlan: (plan: MasterCommunityOrLotPlan) => {
            if (plan.name !== plans.lotCommunityPlan.name) {
              setDropdownMenu(
                dropdownMenu.map((item) => {
                  if (item.value?.toString().startsWith(planId)) {
                    return { ...item, label: plan.name || `${plans.masterPlan.name} on Lot ${plans.lot.lotNumber}` };
                  } else {
                    return item;
                  }
                }),
              );
            }
            assertValidatesSchemaTODO<ClientLotCommunityPlanHydrated>(plan);
            dispatch(planLibraryActions.modifyLotCommunityPlan(plan));
            setUserNotification({
              messageType: "success",
              message: "Lot community plan updated",
            });
          },
          deletePlan: null,
          canArchive: false,
          canUnarchive: false,
          publicPlanType: null,
          showroomPath: lotCommunityPlanShowroomPath(
            builderId,
            plans.masterPlan.id,
            plans.location.id,
            plans.locationPlan.id,
            plans.lot.id,
          ),
          plan: {
            ...plans.lotCommunityPlan,
            name: plans.lotCommunityPlan.name || `${plans.masterPlan.name} on Lot ${plans.lot.lotNumber}`,
          },
        };

      default:
        return {
          updatePlan: null,
          deletePlan: null,
          canArchive: false,
          canUnarchive: false,
          publicPlanType: null,
          showroomPath: null,
          plan: null,
        };
    }
  })();

  const overflowMenuItems = usePlanOverflowMenuItems({
    onDeletePlan: deletePlan ? () => setModalScenario("delete") : null,
    onUpdatePlan: updatePlan ? () => setModalScenario("edit") : null,
    onArchivePlan: canArchive ? () => setModalScenario("archive") : null,
    onUnarchivePlan: canUnarchive ? () => setModalScenario("unarchive") : null,
    onDownloadPlan:
      plans.planType === "master_plan" || plans.planType === "community_plan"
        ? () => setModalScenario("download")
        : null,
    communityPlanId: plans.planType !== "master_plan" ? plans.locationPlan.id : undefined,
    publicPlanType,
    masterPlanId: plans.masterPlan.id,
    showroomPath,
    showroomIntroPath:
      plans.planType !== "master_plan"
        ? showroomIntroPath(
            "community_plans",
            builderId,
            plans.locationPlan.id,
            plans.planType === "lot" ? plans.lot.id : undefined,
          )
        : showroomIntroPath("master_plans", builderId, plans.masterPlan.id),

    editShowroomIntroPath:
      plans.planType !== "master_plan"
        ? editShowroomIntroPath(
            "community_plans",
            builderId,
            plans.locationPlan.id,
            plans.planType === "lot" ? plans.lot.id : undefined,
          )
        : editShowroomIntroPath("master_plans", builderId, plans.masterPlan.id),
    procurePath: procurePath({
      studioDocId: studioDocId ?? undefined,
      builderId,
      communityPlanId: plans.planType === "community_plan" ? plans.locationPlan.id : undefined,
      masterPlanId: plans.planType === "master_plan" ? plans.masterPlan.id : undefined,
    }),
    studioPath: studioPath(studioDocId ?? undefined, builderId),
    masterPlan: plans.masterPlan,
    communityPlan: plans.planType === "community_plan" ? plans.locationPlan : undefined,
    location: plans.planType === "community_plan" ? plans.location : undefined,
    locationSalesAgentId: plans.planType !== "master_plan" ? plans.location.salesStaffId : undefined,
  }) as ReactNode[];

  const initialValue: string = planId;

  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.actionsWrapper}>
          <div className={styles.select}>
            {dropdownMenu.length > 0 && (
              <Select
                ariaLabel="Select Plan"
                value={initialValue}
                options={dropdownMenu}
                onChange={async (value) => {
                  if (value !== initialValue) {
                    const option = dropdownMenu.find((option) => option.value === value);
                    if (option?.pathToRouteTo) {
                      await navigate({ to: option.pathToRouteTo });
                    }
                  }
                }}
              />
            )}
          </div>

          {!isReadOnly && (
            <Tooltip.Root delayDuration={400}>
              <Tooltip.Trigger>
                <span>
                  <Button
                    disabled={!isAnythingOverridden}
                    btnSize="large"
                    btnStyle="remove"
                    onClick={() => dispatch(planLibraryActions.enableDiscardAllOverridesModal(true))}
                    style={{ marginLeft: "10px" }}
                  >
                    <IconBuster style={{ marginRight: "7px" }} size={14} />
                    Discard Overrides
                  </Button>
                </span>
              </Tooltip.Trigger>
              {isAnythingOverridden && (
                <Tooltip.Content>
                  <>
                    Discard all overrides including any option reordering for{" "}
                    {plan?.name ? ` ${plan.name} ` : " this plan"}
                  </>
                </Tooltip.Content>
              )}
            </Tooltip.Root>
          )}

          {!isReadOnly && (
            <Button
              btnStyle={!needsSaving || isSaving ? "remove" : "primary"}
              btnSize="large"
              disabled={!needsSaving || isSaving}
              onClick={async () => {
                const options = planLibraryGetNonZeroBasePriceOptions(
                  nonBoolOptionSetsPerSalesCategory,
                  booleanOptionSetsPerSalesCategory,
                );
                if (options.length > 0) {
                  setNonZeroBasePriceOptions(options);
                } else {
                  void saveOverrides();
                }
              }}
            >
              {isSaving ? "Saving..." : "Save Overrides"}
            </Button>
          )}

          {!isReadOnly && (
            <ConfirmNonZeroBasePriceOptions
              name="Save Overrides"
              mesg="The following base options have a non-zero base price. Continue saving?"
              opts={nonZeroBasePriceOptions}
              onClose={() => {
                setNonZeroBasePriceOptions([]);
              }}
              onConfirm={() => {
                setNonZeroBasePriceOptions([]);
                void saveOverrides();
              }}
            />
          )}
        </div>

        <div className={styles.dropdown}>
          <SimpleDropDownMenu menuItems={overflowMenuItems} />
        </div>
      </div>

      {(() => {
        switch (modalScenario) {
          case "download": {
            if (plans.planType === "master_plan") {
              return (
                <DownloadMasterPlanModal open onClose={() => setModalScenario(undefined)} plan={plan as MasterPlan} />
              );
            } else if (plans.planType === "community_plan") {
              return (
                <DownloadCommunityPlanModal
                  onClose={() => setModalScenario(undefined)}
                  locationPlan={plans.locationPlan}
                />
              );
            }

            return null;
          }
          case "edit":
            if (plans.planType === "master_plan") {
              return (
                <EditMasterPlanModal
                  onClose={() => setModalScenario(undefined)}
                  masterPlan={ensureDefined(plan) as ClientMasterPlan}
                  onSuccess={updatePlan as (mp: ClientMasterPlan) => void}
                />
              );
            }
            return (
              <EditPlanModal
                open
                title={`Edit ${snakeCaseToTitleCase(plans.planType)}`}
                planType={plans.planType}
                plan={ensureDefined(plan)}
                onSubmit={updatePlan}
                onClose={() => setModalScenario(undefined)}
              />
            );
          case "archive":
            assertIsNotNull(plan);
            return (
              <ArchivePlanModal
                open
                onClose={() => setModalScenario(undefined)}
                onConfirm={archivePlan}
                planDetail={snakeCaseToTitleCase(plans.planType)}
                planName={plan.name}
                message={ARCHIVE_PLAN_MESSAGE}
                isArchiving={isArchiving}
              />
            );
          case "unarchive":
            assertIsNotNull(plan);
            return (
              <ArchivePlanModal
                open
                onClose={() => setModalScenario(undefined)}
                onConfirm={unarchivePlan}
                planDetail={snakeCaseToTitleCase(plans.planType)}
                planName={plan.name}
                isArchiving={isArchiving}
                isUnarchive
              />
            );
          case "delete":
            assertIsNotNull(plan);
            return (
              <ConfirmDeleteModal
                entityType={snakeCaseToTitleCase(plans.planType)}
                entityName={plan.name !== null ? plan.name : undefined}
                onClose={() => setModalScenario(undefined)}
                onConfirm={(e) => {
                  e.preventDefault();
                  assertIsNotNull(deletePlan);
                  void deletePlan();
                }}
                isDeleting={isDeleting}
                message={plans.planType === "master_plan" ? DELETE_MASTER_PLAN_MESSAGE : DELETE_COMMUNITY_PLAN_MESSAGE}
              />
            );
          default:
            return null;
        }
      })()}
    </>
  );
}
